import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or number on the wire (matches the convention used across ar.ts / ap.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
const NonNegDecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums
// ============================================================
export const BankStatementStatusSchema = z.enum(['DRAFT', 'RECONCILED']);
export const BankStatementLineStatusSchema = z.enum(['OPEN', 'MATCHED', 'ADJUSTED']);
export const BankReconAdjustmentTypeSchema = z.enum([
    'BANK_FEE',
    'INTEREST',
    'FX',
    'CHECK_NOT_CLEARED',
    'DEPOSIT_IN_TRANSIT',
    'OTHER',
]);
// ============================================================
// BankStatement header
// ============================================================
export const BankStatementSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    bankAccountId: z.uuid(),
    statementDate: z.coerce.date(),
    openingBalance: DecimalLike,
    closingBalance: DecimalLike,
    status: BankStatementStatusSchema,
    fileRef: z.string().nullable().optional(),
    importedBy: z.string().nullable().optional(),
    finalizedAt: z.coerce.date().nullable().optional(),
    finalizedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// POST /bank-statements wire shape — id/status/timestamps are server-derived.
export const CreateBankStatementSchema = z.object({
    bankAccountId: z.uuid(),
    statementDate: z.iso.date(),
    openingBalance: z.coerce.number(),
    closingBalance: z.coerce.number(),
    fileRef: z.string().optional(),
});
// ============================================================
// BankStatementLine — one row per debit/credit on the bank statement
// ============================================================
export const BankStatementLineSchema = z.object({
    id: z.uuid(),
    statementId: z.uuid(),
    lineDate: z.coerce.date(),
    description: z.string().min(1),
    debit: NonNegDecimalLike,
    credit: NonNegDecimalLike,
    reference: z.string().nullable().optional(),
    party: z.string().nullable().optional(),
    status: BankStatementLineStatusSchema,
    matchedJournalLineId: z.uuid().nullable().optional(),
    matchedAt: z.coerce.date().nullable().optional(),
    matchedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Wire shape — bulk POST /lines accepts one of debit/credit (never both).
export const CreateBankStatementLineSchema = z
    .object({
    date: z.iso.date(),
    description: z.string().min(1),
    debit: z.coerce.number().nonnegative().optional(),
    credit: z.coerce.number().nonnegative().optional(),
    ref: z.string().optional(),
    party: z.string().optional(),
})
    .refine((l) => (l.debit ?? 0) > 0 || (l.credit ?? 0) > 0, {
    message: 'Statement line must have a non-zero debit or credit',
});
export const BulkCreateBankStatementLinesSchema = z.object({
    lines: z.array(CreateBankStatementLineSchema).min(1),
});
// ============================================================
// BankReconAdjustment
// ============================================================
export const BankReconAdjustmentSchema = z.object({
    id: z.uuid(),
    statementId: z.uuid(),
    adjustmentType: BankReconAdjustmentTypeSchema,
    amount: DecimalLike,
    description: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
export const CreateBankReconAdjustmentSchema = z.object({
    type: BankReconAdjustmentTypeSchema,
    amount: z.coerce.number(),
    description: z.string().min(1),
    // GL dimensions for the fee/interest JV (Phase 42 optional-3).
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    // If provided, link the adjustment to a specific statement line (clears that
    // line OPEN -> ADJUSTED). Optional — pure book-side items (outstanding check)
    // don't reference a bank line.
    statementLineId: z.uuid().optional(),
    // Stable per-user-action idempotency token (the web generates one per modal
    // open) — replay-gates a double-clicked / retried adjustment server-side.
    idempotencyKey: z.string().min(1).max(200).optional(),
});
// ============================================================
// Match operations
// ============================================================
export const ManualMatchSchema = z.object({
    statementLineId: z.uuid(),
    journalLineId: z.uuid(),
});
export const AutoMatchResultSchema = z.object({
    matched: z.number().int().nonnegative(),
    candidates: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
});
// ============================================================
// Composite shapes
// ============================================================
export const BankStatementWithChildrenSchema = BankStatementSchema.extend({
    lines: z.array(BankStatementLineSchema),
    adjustments: z.array(BankReconAdjustmentSchema),
    bankAccountName: z.string(),
    bankCode: z.string(),
    bankAccNo: z.string(),
});
export const BankStatementListRowSchema = BankStatementSchema.extend({
    bankAccountName: z.string(),
    bankCode: z.string(),
    bankAccNo: z.string(),
    lineCount: z.number().int().nonnegative(),
    matchedCount: z.number().int().nonnegative(),
    openCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=bank-recon.js.map